import {Tree, spawnErrorToast} from '@hm/fundamental'
import type {TreeEvent, TreeNode, TreeState} from '@hm/fundamental'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router'

import {FeatureCatalogItem, FeatureMenuEntry, MenuNodeType} from '../../../api/types/featuresMenu'
import {useUpdateMenu} from '../../../app/features-menu/useUpdateMenu'

import {CustomizeMenuButton} from './CustomizeMenuButton'
import {EditModeActionBar, type EditAction} from './EditModeActionBar'
import {FeatureStoreDialog} from './FeatureStoreDialog'
import {
  applyDroppableTreeNodes,
  applySelectableHomeIcons,
  applyStaticHomeIcons,
  buildNodeLookup,
  collectAddedFeatureIds,
  createDraftFeatureNodeId,
  createDraftFolderNodeId,
  findActiveFeatureLocation,
  findNewlyToggledNodeId,
  hasDuplicateFolderLabels,
  mapTreeNodesToUpdateModel,
  sortByDisplayOrder,
  toNodeId,
  transformFeatureMenuToTreeNodes,
  translateFeatureLabels,
  type EditMode,
  type FeatureMenuNode
} from './features-tree.utils'

interface FeaturesTreeProps {
  featuresMenu: FeatureMenuEntry[]
  homeFeature?: FeatureMenuEntry
  onSelectItem: (menuItem: FeatureMenuNode | undefined) => void
}

export const FeaturesTree: React.FC<FeaturesTreeProps> = ({
  featuresMenu,
  homeFeature,
  onSelectItem
}) => {
  const isMenuCustomizationEnabled = useCommonFeatureFlag(FeatureFlagKey.SuiteMenuCustomization)
  const {mutate: updateMenu, isPending} = useUpdateMenu()
  const {t} = useTranslation()
  const {featureName} = useParams<{featureName: string}>()
  const initialHomeNodeId = homeFeature ? toNodeId(homeFeature) : null

  const activeFeatureLocation = useMemo(
    () => (featureName ? findActiveFeatureLocation(featuresMenu, featureName) : undefined),
    [featuresMenu, featureName]
  )

  const [expanded, setExpanded] = useState<TreeState['expanded']>(() =>
    activeFeatureLocation
      ? Object.fromEntries(activeFeatureLocation.ancestorFolderIds.map((id) => [id, true]))
      : {}
  )
  const [activeId, setActiveId] = useState<TreeState['activeId']>(activeFeatureLocation?.nodeId)
  const [editMode, setEditMode] = useState<EditMode>('off')
  const [draftNodes, setDraftNodes] = useState<TreeState['nodes'] | null>(null)
  const [draftHomeNodeId, setDraftHomeNodeId] = useState<string | null>(null)
  const [nextDraftFolderNumber, setNextDraftFolderNumber] = useState(1)
  const [isFeatureStoreOpen, setIsFeatureStoreOpen] = useState(false)
  const [addedDraftFeatures, setAddedDraftFeatures] = useState<Record<string, FeatureCatalogItem>>(
    {}
  )

  useEffect(() => {
    if (!activeFeatureLocation) return

    setActiveId(activeFeatureLocation.nodeId)

    if (activeFeatureLocation.ancestorFolderIds.length > 0) {
      setExpanded((prev) => ({
        ...prev,
        ...Object.fromEntries(activeFeatureLocation.ancestorFolderIds.map((id) => [id, true]))
      }))
    }
  }, [activeFeatureLocation])

  const baseNodes = useMemo(
    () => transformFeatureMenuToTreeNodes(sortByDisplayOrder(featuresMenu)),
    [featuresMenu]
  )
  const nodeLookup = useMemo(() => buildNodeLookup(featuresMenu), [featuresMenu])
  const effectiveNodeLookup = useMemo(() => {
    const lookup = new Map(nodeLookup)

    Object.entries(addedDraftFeatures).forEach(([nodeId, feature]) => {
      lookup.set(nodeId, {
        id: nodeId,
        type: MenuNodeType.Feature,
        name: feature.name,
        featureId: feature.id
      })
    })

    return lookup
  }, [nodeLookup, addedDraftFeatures])
  const serverNodes = useMemo(
    () =>
      applyDroppableTreeNodes(
        applyStaticHomeIcons(baseNodes, nodeLookup, initialHomeNodeId),
        nodeLookup
      ),
    [baseNodes, nodeLookup, initialHomeNodeId]
  )

  const isEditing = editMode !== 'off'
  const currentNodes = isEditing && draftNodes ? draftNodes : serverNodes
  const addedFeatureIds = useMemo(
    () => collectAddedFeatureIds(currentNodes, effectiveNodeLookup),
    [currentNodes, effectiveNodeLookup]
  )

  const displayState = useMemo(() => {
    const nodes = translateFeatureLabels(
      applyDroppableTreeNodes(currentNodes, effectiveNodeLookup),
      effectiveNodeLookup,
      t
    )

    return {
      nodes,
      expanded,
      activeId
    }
  }, [currentNodes, effectiveNodeLookup, t, expanded, activeId])

  const createDraftFolderNode = useCallback(
    (folderNumber: number): TreeNode => ({
      id: createDraftFolderNodeId(),
      label: `${t('suite.header.newFolder')} ${folderNumber}`,
      children: [],
      isDroppable: true
    }),
    [t]
  )

  const exitEditMode = useCallback(() => {
    setDraftNodes(null)
    setDraftHomeNodeId(null)
    setNextDraftFolderNumber(1)
    setAddedDraftFeatures({})
    setIsFeatureStoreOpen(false)
    setEditMode('off')
  }, [])

  const handleAddFeature = useCallback(
    (feature: FeatureCatalogItem) => {
      if (!draftNodes || addedFeatureIds.has(feature.id)) {
        return
      }

      const nodeId = createDraftFeatureNodeId(feature.id)

      setAddedDraftFeatures((state) => ({...state, [nodeId]: feature}))
      setDraftNodes((state) => (state ? [...state, {id: nodeId, label: feature.name}] : state))
    },
    [addedFeatureIds, draftNodes]
  )

  const handleChange = useCallback(
    (state: TreeState) => {
      setExpanded(state.expanded)
      setActiveId(state.activeId)

      if (editMode === 'off') return
      if (!draftNodes) return

      if (editMode === 'setHomePage') {
        const newlyToggled = findNewlyToggledNodeId(draftNodes, state.nodes)
        if (newlyToggled) {
          setDraftHomeNodeId(newlyToggled)
          setDraftNodes(applySelectableHomeIcons(state.nodes, effectiveNodeLookup, newlyToggled))
          return
        }
      }

      setDraftNodes(state.nodes)
    },
    [editMode, draftNodes, effectiveNodeLookup]
  )

  const handleEvent = useCallback(
    (event: TreeEvent) => {
      if (event.action === 'click') {
        const node: FeatureMenuNode | undefined = effectiveNodeLookup.get(event.nodeId)
        onSelectItem(node)
      }
    },
    [effectiveNodeLookup, onSelectItem]
  )

  const enterEditMode = useCallback(() => {
    setDraftNodes(serverNodes)
    setDraftHomeNodeId(initialHomeNodeId)
    setNextDraftFolderNumber(1)
    setAddedDraftFeatures({})
    setEditMode('edit')
  }, [serverNodes, initialHomeNodeId])

  const handleAction = useCallback(
    (action: EditAction) => {
      switch (action) {
        case 'setHomePage':
          if (editMode === 'setHomePage') {
            setDraftNodes((state) =>
              state ? applyStaticHomeIcons(state, effectiveNodeLookup, draftHomeNodeId) : state
            )
            setEditMode('edit')
          } else {
            setDraftNodes((state) =>
              state ? applySelectableHomeIcons(state, effectiveNodeLookup, draftHomeNodeId) : state
            )
            setEditMode('setHomePage')
          }
          break
        case 'addFolder':
          if (!draftNodes) return

          setDraftNodes((state) => {
            if (!state) return state
            return [...state, createDraftFolderNode(nextDraftFolderNumber)]
          })
          setNextDraftFolderNumber((value) => value + 1)
          break
        case 'addFeature':
          setIsFeatureStoreOpen(true)
          break
        case 'cancel':
          exitEditMode()
          break
        case 'confirm':
          if (!draftNodes) {
            exitEditMode()
            break
          }

          if (hasDuplicateFolderLabels(draftNodes)) {
            spawnErrorToast(t('suite.error.duplicateFolderNames'))
            break
          }

          updateMenu(mapTreeNodesToUpdateModel(draftNodes, effectiveNodeLookup, draftHomeNodeId), {
            onSuccess: exitEditMode
          })
          break
      }
    },
    [
      effectiveNodeLookup,
      createDraftFolderNode,
      draftHomeNodeId,
      draftNodes,
      editMode,
      exitEditMode,
      nextDraftFolderNumber,
      t,
      updateMenu
    ]
  )

  return (
    <>
      {isEditing && (
        <EditModeActionBar
          isHomePageMode={editMode === 'setHomePage'}
          isPending={isPending}
          onAction={handleAction}
        />
      )}
      <Tree
        state={displayState}
        className="feature-tree text-foreground"
        editable={editMode === 'edit'}
        onChange={handleChange}
        onEvent={handleEvent}
      />
      <FeatureStoreDialog
        open={isFeatureStoreOpen}
        addedFeatureIds={addedFeatureIds}
        onAddFeature={handleAddFeature}
        onClose={() => setIsFeatureStoreOpen(false)}
      />
      {!isEditing && isMenuCustomizationEnabled && <CustomizeMenuButton onClick={enterEditMode} />}
    </>
  )
}
